import React, { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';
import cn from 'classnames';
import Bullets from 'slickpay/common/Bullets.component';

import IMG_TAB_TWO_GB from 'assets/slickpay-splash/payment/chair-renter.png';
import IMG_TAB_TWO_US from 'assets/slickpay-splash/payment/chair-renter-dollar.png';
import IMG_TAB_THREE_GB from 'assets/slickpay-splash/payment/commissions.png';
import IMG_TAB_THREE_US from 'assets/slickpay-splash/payment/commissions-dollar.png';
import IMG_TAB_ONE_GB from 'assets/slickpay-splash/payment/employed.png';
import IMG_TAB_ONE_US from 'assets/slickpay-splash/payment/employed-dollar.png';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import CurrencySign from 'components/price/CurrencySign.component';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './PaymentTabs.module.scss';

const LINK_CARD = 'https://gettingslick.typeform.com/SlickPayTerms?utm_source=splash';
const LINK_CUSTOM_QUOTE = 'https://gettingslick.typeform.com/to/wQBtEfq4';
const USE_CTA_BUTTONS = false;

const CardLink = () => {
  const { width: windowWidth } = useWindowSize();
  const { t } = useTranslation();
  const isMobile = windowWidth < MOBILE_VIEWPORT;
  return (
    <ExternalLink link={LINK_CARD} variant="secondary" width={isMobile ? 'max' : ''}>
      <span className={CN.buttonSpace}>{t('slickPay.noReader.paymentTabs.orderButton')}</span>
    </ExternalLink>
  );
};

const TabOne = ({ image }) => {
  const { t } = useTranslation();

  const bullets = [
    <div>
      {t('slickPay.noReader.paymentTabs.employed.bullet1Part1')}
      <span className={CN.newLine}>{t('slickPay.noReader.paymentTabs.employed.bullet1Part2')}</span>
    </div>,
    <div>{t('slickPay.noReader.paymentTabs.employed.bullet2')}</div>,
    <div>
      {t('slickPay.noReader.paymentTabs.employed.bullet3Part1')} <CurrencySign />
      {t('slickPay.noReader.paymentTabs.employed.bullet3Part2')}
    </div>,
  ];

  return (
    <div className={CN.wrap} data-tab="PaymentTabs__TabOne">
      <div className={CN.texContainer}>
        <div className={CN.gap}>
          <div className={CN.title}>{t('slickPay.noReader.paymentTabs.employed.title')}</div>
          <div className={CN.smallTitle}>{t('slickPay.noReader.paymentTabs.employed.subtitle')}</div>
          <div className={CN.badgesContainer}>
            <Badge name={t('slickPay.noReader.paymentTabs.essentialBadge')} variant="plan" noMarginX>
              <Icon variant="essential" />
            </Badge>
            <Badge name={t('slickPay.noReader.paymentTabs.advancedBadge')} variant="plan" noMarginX>
              <Icon variant="advanced" />
            </Badge>
            <Badge name={t('slickPay.noReader.paymentTabs.superchargedBadge')} variant="plan" noMarginX>
              <Icon variant="super" />
            </Badge>
          </div>
          <br />

          <Bullets data={bullets} variantInvert checkmarkWhite />
          {USE_CTA_BUTTONS && (
            <div className={CN.buttonContainer}>
              <CardLink />
              <div className={CN.link}>
                <ExternalLink link={LINK_CUSTOM_QUOTE} variant="primaryForce">
                  <span className={CN.nowrap}>{t('slickPay.noReader.paymentTabs.employed.customQuoteButton')}</span>
                </ExternalLink>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={CN.videoContainer}>
        <Badge variant="outlineForce">
          <div className={CN.videoBadgeText}>{t('slickPay.noReader.paymentTabs.howItWorks')}</div>
        </Badge>

        <div className={CN.video}>
          <img src={image} alt="Employed animation" />
        </div>
      </div>
    </div>
  );
};

const TabTwo = ({ image }) => {
  const { t } = useTranslation();

  const bullets = [
    <div>{t('slickPay.noReader.paymentTabs.renter.bullet1')}</div>,
    <div>{t('slickPay.noReader.paymentTabs.renter.bullet2')}</div>,
    <div>
      <span className={CN.focused}>{t('slickPay.noReader.paymentTabs.renter.bullet3Part1')} </span> {t('slickPay.noReader.paymentTabs.renter.bullet3Part2')}
    </div>,
  ];

  return (
    <div className={CN.wrap} data-tab="PaymentTabs__TabTwo">
      <div className={CN.texContainer}>
        <div className={CN.gap}>
          <div className={CN.title}>{t('slickPay.noReader.paymentTabs.renter.title')}</div>

          <div className={CN.smallTitle}>{t('slickPay.noReader.paymentTabs.renter.subtitle')}</div>
          <div className={CN.badgesContainer}>
            <Badge name={t('slickPay.noReader.paymentTabs.advancedBadge')} variant="plan" noMarginX>
              <Icon variant="advanced" />
            </Badge>
            <Badge name={t('slickPay.noReader.paymentTabs.superchargedBadge')} variant="plan" noMarginX>
              <Icon variant="super" />
            </Badge>
          </div>
          <br />

          <Bullets data={bullets} variantInvert checkmarkWhite />
          {USE_CTA_BUTTONS && (
            <div className={CN.buttonContainer}>
              <CardLink />
            </div>
          )}
        </div>
      </div>
      <div className={CN.videoContainer}>
        <Badge variant="outlineForce">
          <div className={CN.videoBadgeText}>{t('slickPay.noReader.paymentTabs.howItWorks')}</div>
        </Badge>

        <div className={CN.video}>
          <img src={image} alt="Employed animation" />
        </div>
      </div>
    </div>
  );
};

const TabThree = ({ image }) => {
  const { t } = useTranslation();

  const bullets = [
    <div>{t('slickPay.noReader.paymentTabs.commissions.bullet1')}</div>,
    <div>{t('slickPay.noReader.paymentTabs.commissions.bullet2')}</div>,
    <div>{t('slickPay.noReader.paymentTabs.commissions.bullet3')}</div>,
    <div>
      <span className={CN.focused}>{t('slickPay.noReader.paymentTabs.commissions.bullet4Part1')}</span> {t('slickPay.noReader.paymentTabs.commissions.bullet4Part2')}
    </div>,
  ];

  return (
    <div className={CN.wrap} data-tab="PaymentTabs__TabThree">
      <div className={CN.texContainer}>
        <div className={CN.gap}>
          <div className={CN.title}>{t('slickPay.noReader.paymentTabs.commissions.title')}</div>

          <div className={CN.smallTitle}>{t('slickPay.noReader.paymentTabs.commissions.subtitle')}</div>
          <div className={CN.badgesContainer}>
            <Badge name={t('slickPay.noReader.paymentTabs.advancedBadge')} variant="plan" noMarginX>
              <Icon variant="advanced" />
            </Badge>
            <Badge name={t('slickPay.noReader.paymentTabs.superchargedBadge')} variant="plan" noMarginX>
              <Icon variant="super" />
            </Badge>
          </div>
          <br />

          <Bullets data={bullets} variantInvert checkmarkWhite />
          {USE_CTA_BUTTONS && (
            <div className={CN.buttonContainer}>
              <CardLink />
            </div>
          )}
        </div>
      </div>
      <div className={CN.videoContainer}>
        <Badge variant="outlineForce">
          <div className={CN.videoBadgeText}>{t('slickPay.noReader.paymentTabs.howItWorks')}</div>
        </Badge>

        <div className={CN.video}>
          <img src={image} alt="Employed animation" />
        </div>
      </div>
    </div>
  );
};

const PaymentTabs = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { t } = useTranslation();
  const { isSalonInUS } = useGilt();
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < MOBILE_VIEWPORT;

  // automatically open the first tab on resize
  useEffect(() => {
    if (!isMobile) {
      setSelectedTab(0);
    }
  }, [isMobile]);

  const handleTabClick = (index) => {
    if (isMobile) {
      // closes tab on next click on same tab when is mobile
      setSelectedTab(selectedTab === index ? null : index);
    } else {
      setSelectedTab(index);
    }
  };

  const tabData = [
    {
      title: t('slickPay.noReader.paymentTabs.employed.title'),
      content: <TabOne image={isSalonInUS ? IMG_TAB_ONE_US : IMG_TAB_ONE_GB} />,
    },
    {
      title: t('slickPay.noReader.paymentTabs.renter.title'),
      content: <TabTwo image={isSalonInUS ? IMG_TAB_TWO_US : IMG_TAB_TWO_GB} />,
    },
    {
      title: t('slickPay.noReader.paymentTabs.commissions.title'),
      content: <TabThree image={isSalonInUS ? IMG_TAB_THREE_US : IMG_TAB_THREE_GB} />,
    },
  ];

  return (
    <div data-bem="PaymentTabs" className={CN.component}>
      <div data-bem="PaymentTabs__title" className={CN.componentTitle}>
        {t('slickPay.noReader.paymentTabs.title')}
      </div>
      <div data-bem="PaymentTabs__tabs" className={CN.tabsContainer}>
        <div className={CN.tabs}>
          {tabData.map((tab, index) => (
            <div
              key={createNodeKey({ index })}
              className={cn({
                [CN.tab]: true,
                [CN.active]: selectedTab === index,
              })}
              onClick={() => handleTabClick(index)}
            >
              {tab.title}
            </div>
          ))}
        </div>

        {/* Desktop content - renders once below tabs */}
        {!isMobile && selectedTab !== null && (
          <div data-bem="PaymentTabs__tabContent" className={CN.tabContent}>
            {tabData[selectedTab].content}
          </div>
        )}

        {/* Mobile content - renders under each tab */}
        {isMobile && (
          <div data-bem="PaymentTabs__mobileContent" className={CN.mobileContent}>
            {tabData.map((tab, index) => (
              <div key={createNodeKey({ index })} className={CN.mobileTab}>
                <div
                  data-bem="PaymentTabs__mobileTabHeader"
                  className={cn({
                    [CN.mobileTabHeader]: true,
                    [CN.active]: selectedTab === index,
                  })}
                  onClick={() => handleTabClick(index)}
                >
                  {tab.title}
                  <div
                    className={cn({
                      [CN.mobileArrow]: true,
                      [CN.active]: selectedTab === index,
                    })}
                  >
                    <Icon tag="PaymentTabs__arrow" variant="arrowNext" width="lg" selected />
                  </div>
                </div>
                {/* Display content for the selected tab directly below it */}
                {selectedTab === index && <div className={CN.mobileTabContent}>{tab.content}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default PaymentTabs;
